import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/AuthProvider.js";
import { getStudentById, updateStudent } from "../services/studentService.js";
import { getAllClassrooms } from "../services/classroomService.js";
import { collection, query, where, getDocs, getDoc, doc  } from "firebase/firestore";
import { db } from "../firebase.js";
import "../styles/StudentProfilePage.css";

const StudentProfilePage = () => {
  const { t } = useTranslation();
  const { currentUser, logoutUser } = useAuth();
  const navigate = useNavigate();
  
  const [studentData, setStudentData] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    enrollmentNumber: "",
    classroomId: ""
  });

  // Fetch student data, classrooms, and articles on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) {
        navigate("/login");
        return;
      }

      try {
        // Fetch student data
        const student = await getStudentById(currentUser.uid);
        setStudentData(student);
        
        // Initialize form data
        setFormData({
          name: student.name || "",
          enrollmentNumber: student.enrollmentNumber || "",
          classroomId: student.classroomId || ""
        });
        
        // Fetch classrooms
        if (student.classroomId) {
          const clsSnap = await getDoc(doc(db, 'classrooms', student.classroomId));
          setClassrooms(clsSnap.exists() ? [{ id: clsSnap.id, ...clsSnap.data() }] : []);
        }        
        // Fetch student's articles
        if (student.articles && student.articles.length > 0) {
          const articlesRef = collection(db, "news");
          const articlesQuery = query(articlesRef, where("authorId", "==", currentUser.uid));
          const articlesSnapshot = await getDocs(articlesQuery);
          
          const articlesData = articlesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setArticles(articlesData);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(t("studentProfile.errors.fetchError"));
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, navigate, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError("");
    
    try {
      await updateStudent(currentUser.uid, {
        name: formData.name,
        enrollmentNumber: formData.enrollmentNumber,
        classroomId: formData.classroomId || null
      });
      
      // Update local state
      setStudentData(prevData => ({
        ...prevData,
        name: formData.name,
        enrollmentNumber: formData.enrollmentNumber,
        classroomId: formData.classroomId || null
      }));
      
      setSuccess(true);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      setError(t("studentProfile.errors.updateError"));
    }
  };

  const getClassroomName = (classroomId) => {
    if (!classroomId) return t("studentProfile.noClassroom");
    
    const classroom = classrooms.find(c => c.id === classroomId);
    return classroom ? classroom.name : t("studentProfile.unknownClassroom");
  };

  if (loading) {
    return <div className="loading">{t("loading")}</div>;
  }

  if (!currentUser) {
    return <div className="error-message">{t("studentProfile.notLoggedIn")}</div>;
  }

  return (
    <div className="student-profile-container">
      <h1>{t("studentProfile.title")}</h1>
      
      {success && (
        <div className="success-message">
          {t("studentProfile.successMessage")}
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <div className="profile-section">
        <h2>{t("studentProfile.personalInfo")}</h2>
        
        {isEditing ? (
          <form onSubmit={handleSubmit} className="edit-profile-form">
            <div className="form-group">
              <label htmlFor="name">{t("studentProfile.form.name")}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="enrollmentNumber">{t("studentProfile.form.enrollmentNumber")}</label>
              <input
                type="text"
                id="enrollmentNumber"
                name="enrollmentNumber"
                value={formData.enrollmentNumber}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="classroomId">{t("studentProfile.form.classroom")}</label>
              <select
                id="classroomId"
                name="classroomId"
                value={formData.classroomId || ""}
                onChange={handleChange}
              >
                <option value="">{t("studentProfile.form.defaultClassroom")}</option>
                {classrooms.map((classroom) => (
                  <option key={classroom.id} value={classroom.id}>
                    {classroom.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-button">
                {t("studentProfile.form.save")}
              </button>
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    name: studentData.name || "",
                    enrollmentNumber: studentData.enrollmentNumber || "",
                    classroomId: studentData.classroomId || ""
                  });
                }}
              >
                {t("studentProfile.form.cancel")}
              </button>
            </div>
          </form>
        ) : (
          <div className="profile-info">
            <div className="info-row">
              <span className="info-label">{t("studentProfile.email")}:</span>
              <span className="info-value">{studentData.email}</span>
            </div>
            
            <div className="info-row">
              <span className="info-label">{t("studentProfile.name")}:</span>
              <span className="info-value">{studentData.name}</span>
            </div>
            
            <div className="info-row">
              <span className="info-label">{t("studentProfile.enrollmentNumber")}:</span>
              <span className="info-value">{studentData.enrollmentNumber}</span>
            </div>
            
            <div className="info-row">
              <span className="info-label">{t("studentProfile.classroom")}:</span>
              <span className="info-value">{getClassroomName(studentData.classroomId)}</span>
            </div>
            
            <button 
              className="edit-button"
              onClick={() => setIsEditing(true)}
            >
              {t("studentProfile.editButton")}
            </button>
          </div>
        )}
      </div>
      
      <div className="articles-section">
        <h2>{t("studentProfile.myArticles")}</h2>
        
        {articles.length === 0 ? (
          <p className="no-articles">{t("studentProfile.noArticles")}</p>
        ) : (
          <ul className="articles-list">
            {articles.map((article) => (
              <li key={article.id} className="article-item">
                <h3 className="article-title">
                  <a href={`/news/${article.id}`}>{article.title}</a>
                </h3>
                <p className="article-date">{article.date}</p>
                <div className="article-actions">
                  <a href={`/articles/edit/${article.id}`} className="edit-article-link">
                    {t("studentProfile.editArticle")}
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}
        
        <div className="article-actions">
          <a href="/articles/create" className="create-article-button">
            {t("studentProfile.createArticle")}
          </a>
        </div>
      </div>
      
      <div className="logout-section">
        <button 
          className="logout-button"
          onClick={logoutUser}
        >
          {t("header.logout")}
        </button>
      </div>
    </div>
  );
};

export default StudentProfilePage;
